import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar as CalendarIcon, DollarSign, Download, Receipt } from 'lucide-react';
import { useEffect, useState } from 'react';
import { romaneioService } from '../../api';
import { NoOpsSelected, PageError, PageLoading } from '../../components';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { OperationConfirmDialog } from '../../components/ui/operation-confirm-dialog';
import { Label } from '../../components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { StatBox } from '../../components/ui/stat-box';
import { useAuth } from '../../auth/AuthContext';
import { useOps } from '../../hooks/useOps';
import { cn } from '../../lib/utils';
import { useAppSelector } from '../../store';

interface PaymentSummary {
  liquidados: {
    numero_ted_liquidado: number;
    acc_valor_compra: number;
    acc_valor_desagio: number;
    acc_valor_face: number;
  };
  comprados: {
    numero_ted_nao_liquidado: number;
    acc_valor_compra: number;
    acc_valor_desagio: number;
    acc_valor_face: number;
  };
}

export function RomaneioPayment() {
  const { currentOps, getCurrentOpsConfig } = useOps();
  const { hasPermission } = useAuth();
  // Whether the user can download TED files (gates the download buttons; backend enforces too).
  const canDownloadTED = hasPermission('romaneioTEDDownload', currentOps || undefined);
  const { opsConfigs } = useAppSelector((state) => state.ops);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  const [downloadValid] = useState(true);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOpenNL, setConfirmOpenNL] = useState(false);

  const downloadPayment = async (type: 'liquidado' | 'naoLiquidado') => {
    if (!currentOps) {
      return;
    }

    const opsConfig = getCurrentOpsConfig();
    const dbName = opsConfig?.dbName;

    if (!dbName) {
      return;
    }

    try {
      const response = await romaneioService.downloadTED({
        filterDate: selectedDate.format('YYYY-MM-DD'),
        type: type,
        dbName: dbName,
        ops: currentOps,
      });

      if (response.data && response.data.url) {
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download =
          response.data.fileName ||
          `${type}_${selectedDate.format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer download do arquivo');
      console.error('Erro no download:', err);
    }
  };

  useEffect(() => {
    if (!currentOps) {
      return;
    }

    const opsConfig = getCurrentOpsConfig();
    const dbName = opsConfig?.dbName;

    if (!dbName) {
      return;
    }

    const abortController = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await romaneioService.getPaymentSummary({
          filterDate: selectedDate.format('YYYY-MM-DD'),
          dbName: dbName,
          ops: currentOps,
        });

        if (abortController.signal.aborted || !isMounted) {
          return;
        }

        if (response.success && response.data) {
          setPaymentSummary(response.data);
        } else {
          setError(response.message || 'Erro ao carregar dados de pagamento');
        }
      } catch (err: any) {
        if (abortController.signal.aborted || !isMounted) {
          return;
        }
        setError(err.message || 'Erro ao carregar dados de pagamento');
        console.error('Erro ao carregar pagamentos:', err);
      } finally {
        if (!abortController.signal.aborted && isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [currentOps, selectedDate, opsConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentOps) {
    return (
      <NoOpsSelected message="Selecione uma operação para visualizar os dados de pagamento." />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(210,50%,10%)]">TED</h1>
          <p className="text-[hsl(215,16%,47%)]">
            TEDs aprovadas e não aprovadas
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-[hsl(210,50%,10%)] whitespace-nowrap">
            Data para verificar:
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-40 justify-start text-left font-normal border-[hsl(214,32%,91%)] hover:bg-[hsl(210,40%,96%)]',
                  !selectedDate && 'text-[hsl(215,16%,47%)]'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate
                  ? format(selectedDate.toDate(), 'yyyy-MM-dd')
                  : 'Selecione a data'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start">
              <Calendar
                mode="single"
                selected={selectedDate?.toDate()}
                onSelect={(date: Date | undefined) => {
                  if (date) {
                    const newDate = dayjs(date);
                    setConfirmDisabled(false);
                    setPaymentSummary(null);
                    setSelectedDate(newDate);
                  }
                }}
                disabled={(date: Date) => {
                  const minDate = dayjs().subtract(60, 'days').toDate();
                  const maxDate = dayjs().add(1, 'day').toDate();
                  return date < minDate || date > maxDate;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <PageError error={error} />

      <PageLoading loading={loading} />

      {!loading && paymentSummary && (
        <>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <StatBox
                title="Total pagamentos liquidados"
                value={paymentSummary.liquidados.numero_ted_liquidado}
                valueType="integer"
                icon={<Receipt className="h-6 w-6 text-[hsl(210,35%,32%)]" />}
              />
              <StatBox
                title="Valor total de pagamentos realizados (Valor de Compra)"
                value={paymentSummary.liquidados.acc_valor_compra}
                valueType="cents"
                icon={<DollarSign className="h-6 w-6 text-[hsl(142,76%,36%)]" />}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <StatBox
                title="Total valor de Desagio efetuado"
                value={paymentSummary.liquidados.acc_valor_desagio}
                valueType="cents"
                icon={<DollarSign className="h-6 w-6 text-[hsl(43,56%,61%)]" />}
              />
              <StatBox
                title="Total de valor de Face"
                value={paymentSummary.liquidados.acc_valor_face}
                valueType="cents"
                icon={<DollarSign className="h-6 w-6 text-[hsl(210,35%,32%)]" />}
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={!downloadValid || !canDownloadTED}
                className="flex items-center gap-2 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white"
              >
                <Download className="h-4 w-4" />
                Download Liquidados
              </Button>

              <OperationConfirmDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                onConfirm={() => downloadPayment('liquidado')}
                action="download"
                ops={currentOps || ''}
                date={selectedDate.format('YYYY-MM-DD')}
                details={[{ label: 'Tipo', value: 'Pagamentos confirmados (liquidados) da quinzena' }]}
                confirmDisabled={confirmDisabled}
              />
            </div>
          </div>

          <div className="text-center my-8">
            <hr className="border-2 border-dashed border-[hsl(214,32%,91%)]" />
          </div>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <StatBox
                title="Total pagamento NÃO liquidado"
                value={paymentSummary.comprados.numero_ted_nao_liquidado}
                valueType="integer"
                icon={<Receipt className="h-6 w-6 text-[hsl(210,35%,32%)]" />}
              />
              <StatBox
                title="Valor total de pagamento NÃO realizado (Valor de Compra)"
                value={paymentSummary.comprados.acc_valor_compra}
                valueType="cents"
                icon={<DollarSign className="h-6 w-6 text-[hsl(0,65%,50%)]" />}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <StatBox
                title="Total valor de Desagio NÃO efetuado"
                value={paymentSummary.comprados.acc_valor_desagio}
                valueType="cents"
                icon={<DollarSign className="h-6 w-6 text-[hsl(43,56%,61%)]" />}
              />
              <StatBox
                title="Total de valor de Face das NÃO efetuadas"
                value={paymentSummary.comprados.acc_valor_face}
                valueType="cents"
                icon={<DollarSign className="h-6 w-6 text-[hsl(210,35%,32%)]" />}
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => setConfirmOpenNL(true)}
                disabled={!downloadValid || !canDownloadTED}
                variant="outline"
                className="flex items-center gap-2 border-[hsl(214,32%,91%)] hover:bg-[hsl(210,40%,96%)]"
              >
                <Download className="h-4 w-4" />
                Download Não Liquidados
              </Button>

              <OperationConfirmDialog
                open={confirmOpenNL}
                onOpenChange={setConfirmOpenNL}
                onConfirm={() => downloadPayment('naoLiquidado')}
                action="download"
                ops={currentOps || ''}
                date={selectedDate.format('YYYY-MM-DD')}
                details={[{ label: 'Tipo', value: 'Pagamentos NÃO efetuados da quinzena' }]}
                confirmDisabled={confirmDisabled}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
